import { isFooterHiddenRoute, isFooterShownOnPhone } from "../../shared/layout/footer-visibility";
import { Outlet, createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { isValidLocale, switchLanguage, RTL_LOCALES, DEFAULT_LOCALE } from "../../lib/i18n";
import { Header } from "../../shared/layout/header";
import { Footer } from "../../shared/layout/footer";
import { MobileBottomBar } from "../../shared/layout/mobile-bottom-bar";
import { AiPill } from "../../features/ai-concierge";
import { LanguageSuggestionBanner } from "../../shared/i18n/language-suggestion-banner";
import { CookieConsentBanner } from "../../shared/layout/cookie-consent-banner";
import { AppPromo } from "../../shared/app-promo/app-promo";

export const Route = createFileRoute("/$lang/_layout")({
  beforeLoad: async ({ params, location }) => {
    if (!isValidLocale(params.lang)) {
      // Lang-less deep link (e.g. /marrakech/tours/x): keep the full path and
      // prefix the default locale instead of bouncing to the homepage. 301 so
      // crawlers consolidate signal onto the locale-prefixed URL.
      throw redirect({ href: `/${DEFAULT_LOCALE}${location.href}`, statusCode: 301 });
    }
    // Client-only: the browser singleton follows the URL locale. On the
    // server this mutation is forbidden — concurrent SSR requests share the
    // module singleton, and switching it mid-flight leaks one request's
    // language into another's render (which the edge cache then persists).
    // SSR language comes from the per-locale instance in AppRoot instead.
    if (typeof window !== "undefined") {
      // Strings first, then the switch, so no render ever sees English
      // fallbacks under a non-English URL. A no-op when both already match.
      await switchLanguage(params.lang);
    }
  },
  component: LangLayout,
});

function LangLayout() {
  const { lang } = Route.useParams();
  const isRtl = RTL_LOCALES.includes(lang as any);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hasRouteError = useRouterState({
    select: (s) => s.matches.some((match) => match.status === "error" || match.status === "notFound"),
  });
  const isActivityShow = useRouterState({
    select: (s) =>
      s.matches.some(
        (m) =>
          m.routeId === "/$lang/_layout/$city/$category/$slug/" ||
          m.routeId === "/$lang/_layout/$city/$category/$slug" ||
          // A LiteAPI hotel wears the same phone layout (photo to the top
          // edge, floating bar), so it drops the site header the same way.
          m.routeId === "/$lang/_layout/stays/$city/$slug/",
      ),
  });
  // The list lives in `footer-visibility.ts` so it can be tested. As a chain
  // of `endsWith` here it silently missed the chat when it moved off
  // `/concierge`, and the site footer came back under a full-height map.
  const hideFooter = isFooterHiddenRoute(pathname) && !hasRouteError;
  // Below lg the footer shows only on Home and the information pages; an
  // error page keeps it so a lost visitor has somewhere to go.
  const footerPhoneHidden = !hideFooter && !hasRouteError && !isFooterShownOnPhone(pathname);

  // 69px, not 68: the bar is 68 and the pewter rail under it is the 69th.
  const mainClass = pathname.endsWith("/thank-you")
    ? "flex-1 pt-[69px]"
    : isActivityShow
      ? "flex-1 pt-[69px] max-md:pt-0"
      : "flex-1 pt-[69px]";

  /**
   * Room for the fixed bottom bar, at the END of the document and only where
   * the bar exists.
   *
   * Two things were wrong. It sat on `<main>`, and the footer renders after
   * main, so the clearance opened a gap between the last section and the footer
   * rather than below it: 164px of nothing on a phone, on every page with a
   * footer. And it applied at every width, while the bar itself is `lg:hidden`,
   * so a desktop reserved 80px for a bar that is not there.
   *
   * Now it sits on the shell, after the footer, and stops at `lg`. 84px is the
   * bar's own height (a 68px row plus its 12px inset) and not a round number
   * picked to be safe.
   *
   * ONLY where there is no footer. The footer already carries this room itself
   * (`max-lg:pb-[calc(92px + safe-area)]`, see footer.tsx), so adding it here
   * as well stacked two clearances into roughly 176px of white under the
   * copyright. When the footer is hidden, main is the last thing on the page
   * and the shell has to carry it instead.
   *
   * Thank-you keeps none either way: it runs its own bar.
   */
  const shellClass =
    hideFooter && !pathname.endsWith("/thank-you") ? " pb-[84px] lg:pb-0" : "";
  // Same room when the footer is only hidden below lg. Activity and hotel
  // pages have no site bar but their own floating booking bar, whose height
  // varies (75-110px with the safe area, more as a booked card), so the bar
  // publishes it in --mj-floating-bar-h (useFloatingBarRoom). Explore items
  // have no bar, the variable is unset, and no gap opens.
  const phoneShellClass = footerPhoneHidden
    ? isActivityShow
      ? " max-lg:pb-[var(--mj-floating-bar-h,0px)]"
      : " max-lg:pb-[84px]"
    : "";

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className={`flex min-h-screen flex-col bg-bg-body font-sans${shellClass}${phoneShellClass}`}
      style={{ fontFamily: isRtl ? "var(--font-rtl)" : undefined }}
    >
      <div className={isActivityShow ? "max-md:hidden" : undefined}>
        <Header phoneVariant="minimal" />
      </div>
      <main className={mainClass}>
        <Outlet />
      </main>
      {/* On a phone or tablet the footer shows only on Home and the
          information pages (owner, 2026-09-26). Hidden with CSS below lg, so
          its internal links stay in the HTML crawlers read; computers keep
          it everywhere. */}
      {!hideFooter && (footerPhoneHidden ? (
        <div className="max-lg:hidden">
          <Footer />
        </div>
      ) : (
        <Footer />
      ))}
      <MobileBottomBar />
      <AiPill />
      <AppPromo headerless={isActivityShow} />
      <LanguageSuggestionBanner />
      <CookieConsentBanner />
    </div>
  );
}
